import { useRef, useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  InputAdornment,
  CircularProgress,
  IconButton,
  ClickAwayListener,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { useNoteSearch } from '../hooks/useNoteSearch';

interface NoteSearchBarProps {
  onSelect: (note: NoteOut) => void;
  disabled?: boolean;
}

const NoteSearchBar = ({ onSelect, disabled }: NoteSearchBarProps) => {
  const { searchQuery, setSearchQuery, results, isLoading, hasQuery, isError } = useNoteSearch();
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (hasQuery && results.length > 0) {
      setIsOpen(true);
    }
  }, [hasQuery, results.length]);

  const handleSelect = (note: NoteOut) => {
    onSelect(note);
    setSearchQuery('');
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Find a video to vibe with"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (e.target.value.trim().length >= 2) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            if (hasQuery && results.length > 0) {
              setIsOpen(true);
            }
          }}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : searchQuery ? (
                  <IconButton size="small" onClick={handleClear}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.paper',
            },
          }}
        />

        {isOpen && hasQuery && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              maxHeight: 320,
              overflow: 'auto',
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            {isError ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                  Unable to search notes. Please try again.
                </Typography>
              </Box>
            ) : results.length === 0 && !isLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No video notes found matching your search
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {results.map((note) => (
                  <ListItemButton key={note.id} onClick={() => handleSelect(note)} sx={{ py: 1.5 }}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" sx={{ width: 48, height: 36, bgcolor: 'neutral.lightBackground' }}>
                        <VideocamIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {note.title || 'Untitled Note'}
                        </Typography>
                      }
                      secondary={formatDate(note.created_at)}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default NoteSearchBar;
